import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { Loading } from 'components';
import { Subscription } from 'apps/onboarding';
import { useAppManager, useClientStore, useRouterStore } from 'apps/hooks';

import { ChangePlan } from './changePlan';
import { UpgradeManager } from './appManager';

export const UpgradeFlow = observer(() => {
  const routerStore = useRouterStore();
  const clientStore = useClientStore();
  const upgradeManager = useAppManager(UpgradeManager);

  const [isIndividual, setIsIndividual] = useState(
    routerStore.route.params.spacePath === clientStore.signedInProfile.username,
  );
  const [planSelected, setPlanSelected] = useState(
    !!routerStore.route.params.productType,
  );

  useEffect(() => {
    if (!routerStore.route.params.productType) {
      setPlanSelected(false);
    } else {
      setPlanSelected(true);
    }
  }, [routerStore.route.params.productType]);

  const queryCurrentRoleInfo = async () => {
    if (!routerStore.route.params.spacePath || !clientStore.signedInProfile) {
      return;
    }

    if (
      routerStore.route.params.spacePath ===
      clientStore.signedInProfile?.username
    ) {
      upgradeManager.QueryProfileCreditCards(
        clientStore.signedInProfile.username,
      );
    } else {
      upgradeManager.QueryInitiativeCreditCards(
        routerStore.route.params.spacePath,
      );
    }
  };

  useEffect(() => {
    if (!routerStore.route.params.spacePath || !clientStore.signedInProfile) {
      return;
    }

    if (
      routerStore.route.params.spacePath ===
      clientStore.signedInProfile?.username
    ) {
      setIsIndividual(true);
      upgradeManager.QueryProfile(clientStore.signedInProfile.username);
    } else {
      setIsIndividual(false);
      upgradeManager.QueryInitiative(routerStore.route.params.spacePath);
    }
  }, [
    clientStore.signedInProfile?.username,
    routerStore.route.params.spacePath,
  ]);

  useEffect(() => {
    if (upgradeManager.activePlan && routerStore.route.params.productType) {
      if (upgradeManager.activePlan === routerStore.route.params.productType) {
        routerStore.navigate('workspace.upgrade', {
          spacePath: routerStore.route.params.spacePath,
        });
      }
    }
  }, [upgradeManager.activePlan]);

  const { spacePath } = routerStore.route.params;

  if (spacePath.startsWith('@') && !routerStore.route.params.productType) {
    routerStore.navigate('home');

    return null;
  }

  const hiddenStepKeys = isIndividual
    ? ['createTeam', 'addCollaborators']
    : ['createTeam', 'transferProjects'];
  const defaultActiveKey = isIndividual ? 'review' : 'addCollaborators';

  const handleClickCompanyForBusiness = () => {
    setPlanSelected(true);
    routerStore.navigate('workspace.upgrade', {
      spacePath: routerStore.route.params.spacePath,
      productType: 'business',
    });
  };

  const handleClickCompanyForTeam = () => {
    setPlanSelected(true);
    routerStore.navigate('workspace.upgrade', {
      spacePath: routerStore.route.params.spacePath,
      productType: 'pro',
    });
  };

  const data = isIndividual
    ? {
        individual: {
          creditCards: upgradeManager.creditCards,
          billingCycle: upgradeManager.billingCycle,
        },
        onQueryCurrentRoleInfo: queryCurrentRoleInfo,
      }
    : {
        organization: {
          groups: upgradeManager.groups,
          creditCards: upgradeManager.creditCards,
          billingCycle: upgradeManager.billingCycle,
          purchasedSeats: upgradeManager.purchasedSeats,
          includedPlanSeats: upgradeManager.includedPlanSeats,
          freeSeats: upgradeManager.freeSeats,
          unusedSeats: upgradeManager.unusedSeats,
          collaborators: upgradeManager.collaborators,
          teamName: upgradeManager.teamName,
        },
        onQueryCurrentRoleInfo: queryCurrentRoleInfo,
      };

  // @computed is not working, sometime return undefined and then without update
  // so I have to put the condition here
  if (!isIndividual && upgradeManager.initiativeQuery?.loading !== false) {
    return <Loading />;
  }

  if (isIndividual && upgradeManager.profileQuery?.loading !== false) {
    return <Loading />;
  }

  const earlyUpgrade =
    upgradeManager.isEarlyAccess && !upgradeManager.hasBillSubscription;

  return (
    <>
      {planSelected || isIndividual ? (
        <Subscription
          hiddenStepKeys={hiddenStepKeys}
          defaultActiveKey={defaultActiveKey}
          earlyUpgrade={earlyUpgrade}
          currentPeriodEnd={upgradeManager.currentPeriodEnd}
          {...data}
          loading={upgradeManager.loading}
        />
      ) : (
        <ChangePlan
          earlyUpgrade={earlyUpgrade}
          activePlan={upgradeManager.activePlan}
          onClickCompanyForBusiness={handleClickCompanyForBusiness}
          onClickCompanyForTeam={handleClickCompanyForTeam}
        />
      )}
    </>
  );
});
